import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from "react";
import { IconButton, Input } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { formatNumber } from "../app/format";
import { fitView, zoomAt, type Grid } from "./model";

/**
 * 图片查看器：缩放（按钮 / 滚轮 / 双指 / +- 键）、拖动平移（指针 / 方向键）、适配窗口（0 键）、切片网格叠加（列 × 行）。
 * 网格层与图片同一个变换，缩放平移时始终对齐；显示每格的像素尺寸，方便核对精灵图切片。
 */
export function ImageViewer({ src, alt, grid: initialGrid, compact }: { src: string; alt: string; grid: Grid | null; compact?: boolean }) {
  const { t, locale } = useT();
  const box = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ x: 0, y: 0, z: 1 });
  const [showGrid, setShowGrid] = useState(!!initialGrid);
  // 输入框保留原始文本（允许清空后重输），有效值才用于绘制
  const [dims, setDims] = useState(() => ({ cols: String(initialGrid?.cols ?? 4), rows: String(initialGrid?.rows ?? 4) }));
  const num = (s: string, fallback: number) => {
    const n = Math.round(Number(s));
    return Number.isFinite(n) && n >= 1 && n <= 64 ? n : fallback;
  };
  const grid: Grid = { cols: num(dims.cols, initialGrid?.cols ?? 4), rows: num(dims.rows, initialGrid?.rows ?? 4) };
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; z: number } | null>(null);

  useEffect(() => {
    setShowGrid(!!initialGrid);
    if (initialGrid) setDims({ cols: String(initialGrid.cols), rows: String(initialGrid.rows) });
  }, [initialGrid]);

  const fit = useCallback(() => {
    const el = box.current;
    if (!el) return;
    setView(fitView(natural, { w: el.clientWidth, h: el.clientHeight }));
  }, [natural]);

  useEffect(() => {
    fit();
  }, [fit, src]);

  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  const center = () => ({ cx: (box.current?.clientWidth ?? 0) / 2, cy: (box.current?.clientHeight ?? 0) / 2 });
  const zoomBy = (k: number) => {
    const { cx, cy } = center();
    setView((v) => ({ ...v, ...zoomAt(v, v.z * k, cx, cy) }));
  };

  const onWheel = (e: WheelEvent) => {
    const r = box.current!.getBoundingClientRect();
    const k = Math.exp(-e.deltaY * 0.0015);
    setView((v) => ({ ...v, ...zoomAt(v, v.z * k, e.clientX - r.left, e.clientY - r.top) }));
  };

  const onPointerDown = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { d: Math.hypot(a!.x - b!.x, a!.y - b!.y), z: view.z };
      drag.current = null;
    } else drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const r = box.current!.getBoundingClientRect();
      const cx = (a!.x + b!.x) / 2 - r.left;
      const cy = (a!.y + b!.y) / 2 - r.top;
      const z0 = pinch.current.z;
      setView((v) => ({ ...v, ...zoomAt(v, (z0 * d) / pinch.current!.d, cx, cy) }));
      return;
    }
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    drag.current = { ...d, x: e.clientX, y: e.clientY };
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };
  const onPointerUp = (e: PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  const onKey = (e: KeyboardEvent) => {
    const step = 40;
    const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (e.key === "+" || e.key === "=") zoomBy(1.25);
    else if (e.key === "-") zoomBy(0.8);
    else if (e.key === "0") fit();
    else if (moves[e.key]) {
      const [dx, dy] = moves[e.key]!;
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    } else return;
    e.preventDefault();
  };

  const setDim = (k: keyof Grid, v: string) => setDims((d) => ({ ...d, [k]: v }));

  const layer = { width: natural.w || undefined, height: natural.h || undefined, transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})` };
  const gridStyle = {
    ...layer,
    backgroundImage: "linear-gradient(to right, var(--grid-line) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)",
    backgroundSize: `${100 / grid.cols}% ${100 / grid.rows}%`,
  };

  return (
    <div className={`imgview${compact ? " imgview--compact" : ""}`} data-testid="image-viewer">
      <div className="imgview__tools" role="toolbar" aria-label={t("asset.viewer.tools")}>
        <IconButton icon="zoomOut" label={t("asset.viewer.zoom_out")} onClick={() => zoomBy(0.8)} data-testid="zoom-out" />
        <span className="imgview__zoom small mono" aria-live="polite" data-testid="zoom-level">
          {formatNumber(view.z, locale, { style: "percent", maximumFractionDigits: 0 })}
        </span>
        <IconButton icon="zoomIn" label={t("asset.viewer.zoom_in")} onClick={() => zoomBy(1.25)} data-testid="zoom-in" />
        <IconButton icon="maximize" label={t("asset.viewer.fit")} onClick={fit} data-testid="zoom-fit" />
        <span className="grow" />
        {!compact ? (
          <>
            <IconButton icon="grid" label={t(showGrid ? "asset.viewer.grid_hide" : "asset.viewer.grid_show")} aria-pressed={showGrid} onClick={() => setShowGrid((s) => !s)} data-testid="grid-toggle" className={showGrid ? "is-on" : undefined} />
            {showGrid ? (
              <span className="imgview__grid-dims small">
                <label className="ui-sr-only" htmlFor="grid-cols">
                  {t("asset.viewer.cols")}
                </label>
                <Input id="grid-cols" type="number" min={1} max={64} inputMode="numeric" value={dims.cols} onChange={(e) => setDim("cols", e.target.value)} data-testid="grid-cols" />
                <span className="imgview__times" aria-hidden="true" />
                <label className="ui-sr-only" htmlFor="grid-rows">
                  {t("asset.viewer.rows")}
                </label>
                <Input id="grid-rows" type="number" min={1} max={64} inputMode="numeric" value={dims.rows} onChange={(e) => setDim("rows", e.target.value)} data-testid="grid-rows" />
                {natural.w ? (
                  <span className="muted" data-testid="grid-cell">
                    {t("asset.viewer.cell", { w: Math.round((natural.w / grid.cols) * 10) / 10, h: Math.round((natural.h / grid.rows) * 10) / 10 })}
                  </span>
                ) : null}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
      <div
        ref={box}
        className="imgview__stage"
        tabIndex={0}
        role="region"
        aria-label={t("asset.viewer.label", { name: alt })}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKey}
        onDoubleClick={fit}
        data-testid="image-stage"
      >
        <img
          src={src}
          alt={alt}
          className="imgview__img"
          style={layer}
          draggable={false}
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          data-testid="asset-image"
        />
        {showGrid ? <div className="imgview__grid" style={gridStyle} data-cols={grid.cols} data-rows={grid.rows} data-testid="grid-overlay" aria-hidden="true" /> : null}
      </div>
    </div>
  );
}
